import "./App.css";

import DocumentTitle from "@components/DocumentTitle";
import ErrorBoundary from "@components/ErrorBoundary";
import Footer from "@components/Footer";
import LoadingSpinner from "@components/LoadingSpinner";
import Navigation from "@components/Navigation";
import Notification from "@components/Notification";
import SchemaMarkup from "@components/SchemaMarkup";
import { AppProvider, useAppContext } from "@contexts/AppContext";
import { Fab } from "@material-ui/core";
import AboutPage from "@pages/AboutPage";
import AccessibilityPage from "@pages/AccessibilityPage";
import CartPage from "@pages/CartPage";
import CityLandingPage from "@pages/CityLandingPage";
import ContactPage from "@pages/ContactPage";
import GalleryPage from "@pages/GalleryPage";
import HomePage from "@pages/HomePage";
import NationalInsurancePage from "@pages/NationalInsurancePage";
import ObituaryPage from "@pages/ObituaryPage";
import OrderPage from "@pages/OrderPage";
import PrivacyPage from "@pages/PrivacyPage";
import ProjectsPage from "@pages/ProjectsPage";
import RefurbishmentPage from "@pages/RefurbishmentPage";
import ScheduleRabiPage from "@pages/ScheduleRabiPage";
import ServicesPage from "@pages/ServicesPage";
import StoneTypeLandingPage from "@pages/StoneTypeLandingPage";
import TermsPage from "@pages/TermsPage";
import TombstonePage from "@pages/TombstonePage";
import { ThemeProvider } from "@providers";
import useLanguageStore from "@stores/languageStore";
import { useEffect, useId } from "react";
import { HelmetProvider } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";

const ScrollToTop = () => {
	const { pathname } = useLocation();

	useEffect(() => {
		// Ensure we reset scroll whenever a new route is pushed
		window.scrollTo({ top: 0, left: 0, behavior: "auto" });

		// Move focus to main content for accessibility
		const mainContent = document.getElementById("main-content");
		if (mainContent) {
			mainContent.focus();
		}
	}, [pathname]);

	return null;
};

const AppContent = () => {
	const { loading } = useAppContext();
	const { t } = useTranslation();
	const { language } = useLanguageStore();
	const isRtl = language === "he";
	const mainContentId = "main-content";

	// Detect mobile device for WhatsApp link
	const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
	const whatsappLink = isMobile
		? "whatsapp://send?phone=972505774257"
		: "https://wa.me/972505774257";

	// Function to handle WhatsApp click conversion
	const handleWhatsAppClick = (e) => {
		if (window.gtag_report_conversion) {
			window.gtag_report_conversion(whatsappLink);
		}
	};

	return (
		<>
			<ScrollToTop />
			<DocumentTitle />
			{/* Skip to main content link for accessibility */}
			<a
				href={`#${mainContentId}`}
				className="sr-only"
				style={{
					position: "absolute",
					left: "-9999px",
					zIndex: 9999,
				}}
				onFocus={(e) => {
					e.target.style.position = "fixed";
					e.target.style.left = isRtl ? "auto" : "1rem";
					e.target.style.right = isRtl ? "1rem" : "auto";
					e.target.style.top = "1rem";
					e.target.style.background = "#57534e";
					e.target.style.color = "white";
					e.target.style.padding = "0.5rem 1rem";
					e.target.style.borderRadius = "4px";
					e.target.style.textDecoration = "none";
					e.target.style.outline = "2px solid white";
					e.target.style.outlineOffset = "2px";
				}}
				onBlur={(e) => {
					e.target.style.position = "absolute";
					e.target.style.left = "-9999px";
				}}
			>
				{t("common.skipToContent")}
			</a>
			<SchemaMarkup />
			<Navigation />
			{loading && <LoadingSpinner fullScreen />}
			<Notification />
			<main id={mainContentId} tabIndex="-1" style={{ outline: "none" }}>
				<Routes>
					<Route path="/" element={<HomePage />} />
					<Route path="/about" element={<AboutPage />} />
					<Route path="/gallery" element={<GalleryPage />} />
					<Route path="/projects" element={<ProjectsPage />} />
					<Route path="/services" element={<ServicesPage />} />
					<Route path="/obituary" element={<ObituaryPage />} />
					<Route path="/tombstone" element={<TombstonePage />} />
					<Route path="/schedule-rabbi" element={<ScheduleRabiPage />} />
					<Route path="/contact" element={<ContactPage />} />
					<Route path="/refurbish" element={<RefurbishmentPage />} />
					<Route path="/cart" element={<CartPage />} />
					<Route path="/order" element={<OrderPage />} />
					<Route path="/accessibility" element={<AccessibilityPage />} />
					<Route path="/privacy" element={<PrivacyPage />} />
					<Route path="/terms" element={<TermsPage />} />
					<Route path="/national-insurance" element={<NationalInsurancePage />} />
					<Route path="/cities/:cityName" element={<CityLandingPage />} />
					<Route path="/stones/:stoneSlug" element={<StoneTypeLandingPage />} />
				</Routes>
			</main>
			<Footer />
			{/* WhatsApp Floating Button */}
			<Fab
				component="a"
				href={whatsappLink}
				onClick={handleWhatsAppClick}
				target={isMobile ? "_self" : "_blank"}
				rel="noopener noreferrer"
				aria-label={t("common.whatsapp")}
				style={{
					position: "fixed",
					bottom: "24px",
					right: isRtl ? "auto" : "24px",
					left: isRtl ? "24px" : "auto",
					zIndex: 1400,
					backgroundColor: "#25D366",
					color: "white",
				}}
				className="whatsapp-float"
			>
				<svg
					width="24"
					height="24"
					viewBox="0 0 24 24"
					fill="currentColor"
					xmlns="http://www.w3.org/2000/svg"
					aria-hidden="true"
				>
					<path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
				</svg>
			</Fab>
		</>
	);
};

const App = () => (
	<ErrorBoundary>
		<HelmetProvider>
			<AppProvider>
				<ThemeProvider>
					<BrowserRouter>
						<AppContent />
					</BrowserRouter>
				</ThemeProvider>
			</AppProvider>
		</HelmetProvider>
	</ErrorBoundary>
);

export default App;
